import {
  AntDesign,
  Ionicons,
  MaterialIcons,
  MaterialCommunityIcons,
} from "@expo/vector-icons";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useNavigation } from "@react-navigation/native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useEffect, useState } from "react";
import { ScrollView } from "react-native";
import { Colors, GridList, LoaderScreen, Spacings } from "react-native-ui-lib";
import Product from "../../../../utilities/data/model/Product";
import { useFullTextSearchProvider } from "../../../../utilities/fulltext-search";
import { toTitleCase } from "../../../../utilities/to-title-case";
import { useLayoutProvider } from "../../utils/layout";
import { useAddItem } from "../../utils/sale-controller/hooks";
import { QuickKey } from "../QuickKey";
import { useOfflineProductSearch } from "../../../../utilities/product";
import { SkeletonView } from "@towersystems/roam-common-native";
import React from "react";
import { GIFTCARD_PRODUCT_SKU } from "../../../../utilities/gift-card/constants";

const PRODUCT_LIMIT = 16;

export const DefaultProductQuickKeyList = () => {
  const { items, query, executedQuery, loading, clearSearchValue } =
    useFullTextSearchProvider();

  const [internalLoading, setInternalLoading] = React.useState(false);

  const {
    data: offlineItems,
    loading: offlineLoading,
    searchProducts,
  } = useOfflineProductSearch();

  const { layoutDimensions, setWidth } = useLayoutProvider();
  const database = useDatabase();
  const [products, setProducts] = useState<Product[]>([]);
  const online = useOnline();
  const isEmptyQuery = query === "" || !query;
  const navigation = useNavigation();
  const addItem = useAddItem({ navigation: navigation });

  function normalizeData() {
    if (isEmptyQuery) {
      return {
        items: [],
        loading: false,
      };
    } else {
      if (online) {
        return {
          items: items?.filter((i: any) => {
            // Gift card should be searchable if eneabled
            if (i.entity.internal && i.entity.stockCode === "GIFTCARD") {
              return true;
            }
            return i.entity.active && i.entity.internal == false;
          }),
          loading,
        };
      } else {
        return {
          items: offlineItems,
          loading: offlineLoading,
        };
      }
    }
  }

  const normalizedData = React.useMemo(() => {
    return normalizeData();
  }, [items, offlineItems, offlineLoading]);

  useEffect(() => {
    if (normalizedData.items?.length === 0) {
      setProducts([]);
      setInternalLoading(false);
      return;
    } else {
      database
        .get<Product>("products")
        .query(
          Q.where("active", Q.eq(true)),
          Q.where(
            "id",
            Q.oneOf(normalizedData.items?.map((i: any) => i.id) ?? [])
          ),
          Q.take(PRODUCT_LIMIT)
        )
        .fetch()
        .then((i) => {
          setProducts(i);
        })
        .catch((e) => console.log(e))
        .finally(() => setInternalLoading(false));
    }
  }, [normalizedData.items]);

  useEffect(() => {
    if (!online) {
      setInternalLoading(true);
      searchProducts(query, { limit: PRODUCT_LIMIT });
    }
  }, [online, query]);

  const addItemToSale = async (item: Product) => {
    const isProductKit = !!item.productKit.id;

    return addItem(item.id, { skipPriceCheck: isProductKit })
      .then(() => clearSearchValue())
      .catch((e) => console.log(e));
  };

  const renderItem = ({
    item,
  }: {
    item: {
      product?: Product;
      action?: "UNKNOWN_PRODUCT";
    };
  }) => {
    const product = item?.product;

    if (item?.action === "UNKNOWN_PRODUCT") {
      const subtitleStr = online ? executedQuery : query;
      return (
        <QuickKey
          iconPrimary={
            <Ionicons name="create-outline" size={24} color="black" />
          }
          label={toTitleCase("Add Custom product")}
          subtitle={`Add "${subtitleStr}" to cart`}
          canTriggerEdit={false}
          onPress={() => {
            //@ts-ignore
            navigation.navigate("ActionsRootScreen", {
              screen: "AddNonMatchedProductScreen",
              params: {
                barcode: subtitleStr,
              },
            });

            // clear the search value
            clearSearchValue();
          }}
        ></QuickKey>
      );
    }

    if (!product) return null;
    const subtitle = product?.hasVariants
      ? `${product.totalVariants} variations`
      : product?.variantParentId
      ? product?.displayName
      : undefined;
    let icon = null;

    if (product?.hasVariants) {
      icon = <AntDesign name="tagso" size={24} color={Colors.text} />;
    } else if (product.serialNumbersSale) {
      icon = <AntDesign name="barcode" size={24} color={Colors.text} />;
    }
    if (product.stockCode == GIFTCARD_PRODUCT_SKU) {
      icon = (
        <MaterialIcons name="card-giftcard" size={24} color={Colors.text} />
      );
    } else if (!!product.isKit) {
      icon = (
        <MaterialCommunityIcons
          name="shopping-outline"
          size={24}
          color={Colors.text}
        />
      );
    }

    return (
      <QuickKey
        label={toTitleCase(product.name)}
        canTriggerEdit={false}
        subtitle={subtitle}
        onPress={() => addItemToSale(product)}
        backgroundImage={product.imageUrl}
        iconPrimary={icon}
        labelStyle={{ color: product?.imageUrl ? Colors.white : undefined }}
      ></QuickKey>
    );
  };

  const loadingMarkup =
    layoutDimensions?.width &&
    online &&
    (normalizedData.loading || internalLoading) ? (
      <GridList
        contentContainerStyle={{ width: "100%" }}
        nestedScrollEnabled={true}
        data={Array.from({ length: 16 }, (_, index) => ({ key: index }))}
        numColumns={
          layoutDimensions.width <= 1000
            ? layoutDimensions.width <= 700
              ? 2
              : 3
            : 4
        }
        renderItem={() => (
          <SkeletonView
            colors={[Colors.grey60, Colors.grey50, Colors.grey60]}
            height={165}
          />
        )}
        containerWidth={layoutDimensions.width}
      ></GridList>
    ) : undefined;

  const data: any = [
    ...products.map((p) => ({
      product: p,
    })),
  ];

  const matchingProductQuery = products.some((item) => {
    return item.stockNumber === executedQuery;
  });

  // if the query is not empty and there is no matching product in the list
  // this means that the user is trying to add a product that is not in the list
  if (
    (executedQuery && !matchingProductQuery) ||
    (!isEmptyQuery && false == online)
  ) {
    data.unshift({
      action: "UNKNOWN_PRODUCT",
    });
  }

  const productsMarkup =
    layoutDimensions?.width &&
    !loading &&
    !internalLoading &&
    !loadingMarkup ? (
      <GridList
        contentContainerStyle={{ width: "100%" }}
        nestedScrollEnabled={true}
        data={data}
        keyExtractor={(item) => item?.product?.id ?? item?.action ?? ""}
        numColumns={
          layoutDimensions.width <= 1000
            ? layoutDimensions.width <= 700
              ? 2
              : 3
            : 4
        }
        renderItem={renderItem}
        containerWidth={layoutDimensions.width}
      ></GridList>
    ) : undefined;

  return (
    <ScrollView
      style={{ marginTop: Spacings.s7 }}
      nestedScrollEnabled={true}
      onLayout={(event) => {
        const { width } = event.nativeEvent.layout;
        setWidth(width);
      }}
    >
      {loadingMarkup || productsMarkup}
    </ScrollView>
  );
};
